import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Presence } from '@radix-ui/react-presence';
import { type Primitive } from '@radix-ui/react-primitive';
import { type ComponentProps, type ComponentRef, forwardRef, memo, useMemo } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { cn } from '@air/utils-tailwind';

import { getPlayerPreviewState } from '../utils/getPlayerPreviewState';
import {
  PLAYER_PREVIEW_CONTENT_NAME,
  type PlayerPreviewScopeProps,
  usePlayerPreviewContext,
} from './PlayerPreviewContextProvider';

export type PlayerPreviewContentProps = ComponentProps<'div'> & {
  forceMount?: boolean;
};

export const PlayerPreviewContent = memo(
  forwardRef<ComponentRef<typeof Primitive.div>, PlayerPreviewScopeProps<PlayerPreviewContentProps>>(
    ({ __scopePlayerPreview, className, forceMount, style, ...restOfProps }, forwardedRef) => {
      const context = usePlayerPreviewContext(PLAYER_PREVIEW_CONTENT_NAME, __scopePlayerPreview);
      const composedRefs = useComposedRefs(forwardedRef, context.contentRef);
      const { width: contentWidth } = useResizeDetector({
        handleHeight: false,
        targetRef: context.contentRef,
      });
      const { width: triggerWidth } = useResizeDetector({
        handleHeight: false,
        targetRef: context.triggerRef,
      });

      const leftPosition = useMemo(() => {
        if (!contentWidth || !triggerWidth) {
          return `${context.position * 100}%`;
        }

        const x = context.position * triggerWidth;

        /**
         * These checks are to ensure the preview doesn't overflow the container.
         */
        if (x < contentWidth / 2) {
          return `${contentWidth / 2}px`;
        } else if (x > triggerWidth - contentWidth / 2) {
          return `${triggerWidth - contentWidth / 2}px`;
        }

        return `${x}px`;
      }, [context.position, contentWidth, triggerWidth]);

      return (
        <Presence present={forceMount || context.open}>
          <div
            className={cn(
              'pointer-events-none absolute bottom-full z-20 -translate-x-1/2 -translate-y-8 overflow-hidden rounded-8 bg-black shadow-popover ring-2 ring-white/80 data-[state=open]:block data-[state=closed]:hidden',
              className,
            )}
            data-state={getPlayerPreviewState(context.open)}
            data-testid="PLAYER_PREVIEW_CONTENT"
            style={{
              left: leftPosition,
              ...style,
            }}
            {...restOfProps}
            ref={composedRefs}
          />
        </Presence>
      );
    },
  ),
);

PlayerPreviewContent.displayName = 'PlayerPreviewContent';
